<template>
  <div
    class="engagement-bar engagement-bar--status"
    :class="{
      'engagement-bar--status-reply': isReplyToStatus,
      'engagement-bar--condensed': isCondensed
    }"
  >
    <div class="engagement-bar__actions" data-test-id="status-engagement-bar-actions">
      <reaction-menu
        :id="statusOrReplyId"
        :content-type="statusOrStatusReplyType"
        :is-condensed="isCondensed"
      />
      <button
        class="engagement-bar__action"
        data-test-id="status-engagement-bar-button"
        :aria-expanded="showForm ? 'true' : 'false'"
        :aria-controls="statusReplyFormId"
        @click="toggleReplyForm"
      >
        <icon-component name="reply-arrow" class="engagement-bar__action-icon" />
        <span class="engagement-bar__action-text">reply</span>
      </button>
      <context-menu :is-by-current-user="isByCurrentUser">
        <template v-slot:admin>
          <context-menu-option
            icon="trash-can"
            @click="handleModeratorDelete"
          >Delete</context-menu-option>
          <context-menu-option
            icon="pencil"
            @click="openModerationContentFlag"
          >Edit</context-menu-option>
        </template>
        <template v-slot:moderator>
          <context-menu-option
            icon="flag"
            @click="markAsSpam"
          >Mark as spam</context-menu-option>
        </template>
        <template v-slot:currentUser>
          <context-menu-option
            icon="trash-can"
            @click="handleDeleteAction"
          >Delete</context-menu-option>
        </template>
        <template>
          <context-menu-option
            icon="flag"
            @click="reportContentOrUser"
          >Report</context-menu-option>
        </template>
      </context-menu>
    </div>
    <status-reply-form
      v-if="showForm"
      :id="statusReplyFormId"
      ref="replyForm"
      :status="status"
      :is-child-reply="isChildReply"
      :reply="reply"
      :parent-reply-id="parentReplyToId"
      :limit="limit"
      @submitted="handleSubmit"
    />
  </div>
</template>

<script>
import ContextMenu from '@/components/Engagement/ContextMenu.vue';
import ContextMenuOption from '@/components/Engagement/ContextMenuOption.vue';
import ReactionMenu from '@/components/Reactions/ReactionMenu.vue';
import StatusReplyForm from '@/components/User/Statuses/StatusReplyForm.vue';
import moderationActionsMixin from '@/mixins/moderation-actions-mixin';
import { typeToAssocType } from '@/utils/moderation-helpers';
import { DELETE_USER_STATUS, DELETE_USER_STATUS_REPLY } from '@/graphql/mutations/status-mutation';
import { mapGetters } from 'vuex';

export default {
  name: 'StatusEngagementBar',
  components: {
    ContextMenu,
    ContextMenuOption,
    ReactionMenu,
    StatusReplyForm,
  },
  mixins: [moderationActionsMixin],
  props: {
    status: {
      type: Object,
      required: true,
    },
    // This prop is necessary for StatusReplyForm
    limit: {
      type: Number,
      default: null,
    },
    reply: {
      type: Object,
      default: () => {},
    },
    isChildReply: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showForm: false,
    };
  },
  computed: {
    ...mapGetters([
      'userIsLoggedIn',
      'currentUser',
    ]),
    isCondensed() {
      return this.isReplyToStatus;
    },
    form() {
      return this.$refs.replyForm;
    },
    moderation() {
      if (this.isReplyToStatus) {
        return {
          assocId: this.reply.id,
          assocType: typeToAssocType('user_profile_status_reply'),
        };
      }
      return {
        assocId: this.status.id,
        assocType: typeToAssocType('user_profile_status'),
      };
    },
    isReplyToStatus() {
      return this.reply && Object.keys(this.reply).length > 0;
    },
    userForMention() {
      if (this.isReplyToStatus) {
        return {
          id: this.reply.user.id,
          username: this.reply.user.username,
        };
      }
      return {
        id: this.status.user.id,
        username: this.status.user.username,
      };
    },
    isByCurrentUser() {
      if (this.isReplyToStatus && this.reply.user) {
        return this.reply.user.id === this.currentUser.id;
      }
      return this.status.user.id === this.currentUser.id;
    },
    userForMentionIsValid() {
      return !!(this.userForMention.id && this.userForMention.username);
    },
    parentReplyToId() {
      return this.status.inReplyTo ? this.status.inReplyTo.id : this.status.id;
    },
    statusOrReplyId() {
      return this.isReplyToStatus ? this.reply.id : this.status.id;
    },
    statusReplyFormId() {
      return `status-reply-form-${this.statusOrReplyId}`;
    },
    statusOrStatusReplyType() {
      return this.isReplyToStatus ? 'statusReply' : 'status';
    },
  },
  methods: {
    toggleReplyForm(event) {
      if (!this.userIsLoggedIn) {
        this.$store.dispatch('openRegisterDialog');
        return;
      }
      this.$snowplow.trackButtonEvent({
        data: {
          type: 'status',
          text: event.target.textContent.trim(),
          classes: event.target.classList,
          id: event.target.id,
        },
      });
      this.showForm = !this.showForm;
      if (this.showForm) {
        this.$nextTick(() => {
          this.openMentionbox();
          this.maybeInjectMentionToForm();
        });
      }
    },
    openMentionbox() {
      this.form.$refs.mentionBox.openMentionbox();
    },
    maybeInjectMentionToForm() {
      if (this.userForMentionIsValid) {
        this.$nextTick(() => {
          this.form.$refs.mentionBox.injectMention(this.userForMention);
        });
      }
    },
    handleSubmit() {
      this.showForm = false;
    },
    handleModeratorDelete() {
      this.redirectFromSingleStatusRoute().then(() => {
        this.moderatorDelete();
      });
    },
    handleDeleteAction() {
      if (!this.isByCurrentUser) {
        this.$store.dispatch('addToastNotification', {
          toastType: 'error',
          description: 'Not authorized',
        });
        return;
      }
      if (this.isReplyToStatus) {
        this.deleteStatusReply();
        return;
      }
      this.redirectFromSingleStatusRoute().then(() => {
        this.deleteStatus();
      });
    },
    redirectFromSingleStatusRoute() {
      return new Promise((resolve) => {
        if (this.$route.params.id === this.status.id) {
          if (this.$route.name === 'MemberProfileStatusById') {
            this.$router.push({ name: 'memberProfileStatus' });
          }
        }
        resolve();
      });
    },
    deleteStatusReply() {
      this.$apollo.mutate({
        mutation: DELETE_USER_STATUS_REPLY,
        variables: {
          id: this.reply.id,
        },
      }).then(() => {
        this.$store.dispatch('addToastNotification', {
          toastType: 'success',
          description: 'Your status update reply has been deleted.',
          type: 'Status Update',
        });
        this.$router.go();
      }).catch(() => {
        this.$store.dispatch('addGenericErrorNotification');
      });
    },
    deleteStatus() {
      this.$apollo.mutate({
        mutation: DELETE_USER_STATUS,
        variables: {
          id: this.status.id,
        },
      }).then(() => {
        this.$store.dispatch('addToastNotification', {
          toastType: 'success',
          description: 'Your status update has been deleted.',
          type: 'Status Update',
        });
        this.$router.go();
      }).catch(() => {
        this.$store.dispatch('addGenericErrorNotification');
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_engagement-bar';
</style>

<docs>
</docs>
